import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";

export interface PersonenOption<T extends string | number> {
  id: T;
  label: string;
  hint?: string;
}

function kuerzel(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1];
  return `${first[0] || ""}${last?.[0] || ""}`.toUpperCase();
}

export function MerosMitarbeiterWahl<T extends string | number>({
  options,
  selected,
  disabled,
  onChange,
  placeholder = "Mitarbeiter suchen …",
  emptyText = "Keine Mitarbeiter im Montageplan.",
}: {
  options: PersonenOption<T>[];
  selected: T[];
  disabled?: boolean;
  onChange: (ids: T[]) => void;
  placeholder?: string;
  emptyText?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function closeIfOutside(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("pointerdown", closeIfOutside);
    return () => document.removeEventListener("pointerdown", closeIfOutside);
  }, [open]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const chosen = useMemo(
    () => options.filter((row) => selectedSet.has(row.id)),
    [options, selectedSet],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((row) => {
      if (selectedSet.has(row.id)) return false;
      const haystack = `${row.label} ${row.hint || ""}`.toLowerCase();
      return !q || haystack.includes(q);
    });
  }, [options, query, selectedSet]);

  function add(id: T) {
    if (selectedSet.has(id)) return;
    onChange([...selected, id]);
    setQuery("");
    setCursor(0);
  }

  function remove(id: T) {
    onChange(selected.filter((item) => item !== id));
  }

  if (options.length === 0) {
    return <p className="text-sm text-label-secondary">{emptyText}</p>;
  }

  return (
    <div ref={rootRef} className="relative">
      <div
        className={clsx(
          "flex min-h-11 flex-wrap items-center gap-1.5 rounded-xl border border-separator bg-card px-2 py-1.5 shadow-sm",
          "focus-within:border-accent focus-within:ring-2 focus-within:ring-accent-soft",
          disabled && "opacity-60",
        )}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
      >
        {chosen.map((row) => (
          <span
            key={String(row.id)}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-fill-secondary py-0.5 pl-0.5 pr-1 text-sm text-label"
          >
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[10px] font-semibold text-accent"
              aria-hidden
            >
              {kuerzel(row.label)}
            </span>
            <span className="truncate">{row.label}</span>
            <button
              type="button"
              aria-label={`${row.label} entfernen`}
              disabled={disabled}
              className="rounded-full px-1 text-label-tertiary hover:bg-fill-tertiary hover:text-label disabled:pointer-events-none"
              onClick={(event) => {
                event.stopPropagation();
                remove(row.id);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          disabled={disabled}
          className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm text-label placeholder:text-label-tertiary focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
          placeholder={chosen.length === 0 ? placeholder : ""}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setCursor(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={(event) => {
            const next = event.relatedTarget;
            if (next instanceof Node && rootRef.current?.contains(next)) return;
            setOpen(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setOpen(true);
              setCursor((value) => Math.min(value + 1, Math.max(filtered.length - 1, 0)));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setCursor((value) => Math.max(value - 1, 0));
            } else if (event.key === "Enter" && filtered[cursor]) {
              event.preventDefault();
              add(filtered[cursor].id);
            } else if (event.key === "Backspace" && !query) {
              const last = chosen[chosen.length - 1];
              if (!last) return;
              event.preventDefault();
              remove(last.id);
            } else if (event.key === "Escape") {
              setOpen(false);
            }
          }}
        />
      </div>
      {open && !disabled ? (
        <ul
          role="listbox"
          className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-xl border border-separator bg-card py-1 text-sm shadow-ios-md"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-label-tertiary">
              {query.trim() ? "Kein Treffer." : "Alle ausgewählt."}
            </li>
          ) : (
            filtered.map((row, index) => (
              <li key={String(row.id)} role="option" aria-selected={index === cursor}>
                <button
                  type="button"
                  className={clsx(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left text-label",
                    index === cursor ? "bg-fill-tertiary" : "hover:bg-fill-tertiary",
                  )}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    add(row.id);
                  }}
                  onMouseEnter={() => setCursor(index)}
                >
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent"
                    aria-hidden
                  >
                    {kuerzel(row.label)}
                  </span>
                  <span className="truncate">{row.label}</span>
                  {row.hint ? (
                    <span className="shrink-0 text-xs text-label-tertiary">{row.hint}</span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
